import { NgClass, NgOptimizedImage } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  NgZone,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import KeenSlider, { KeenSliderInstance } from 'keen-slider';
import { Card, CarouselConfig, TitleCarousel } from 'src/types/builder-components/carousel';
import { CAROUSEL_SCHEMA } from 'src/types/builder-components/carousel-schema';
import { ScreenSizeService } from '../../../services/screen-size.service';
import { AppHomeTopCarouselItemComponent } from '../home-top-carousel-item/home-top-carousel-item.component';

@Component({
  standalone: true,
  imports: [AppHomeTopCarouselItemComponent, NgClass, NgOptimizedImage],
  selector: 'app-home-top-carousel',
  templateUrl: './home-top-carousel.component.html',
  styleUrls: ['./home-top-carousel.styles.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppHomeTopCarouselComponent implements OnDestroy {
  @ViewChild('sliderRef') sliderRef!: ElementRef<HTMLElement>;
  hide = input<boolean>(false);
  title = input<TitleCarousel>();
  carouselConfig = input<CarouselConfig>();
  cards = input.required<Card[]>();

  #screenSizeService = inject(ScreenSizeService);
  #zone = inject(NgZone);

  // TODO: We need to document the validation system used in the Page Component of Builder. We will use the documentation in the gitlab pages
  static inputsSchemaValidation = CAROUSEL_SCHEMA; // Schema used to validate the Builder data model against the inputs of the component

  slider!: KeenSliderInstance;
  slidesNumber: number = 0;
  slidesPerView: number = 4;
  showArrows = signal(true);
  showDots = signal(true);
  currentSlide = signal(0);
  dotHelper: Array<Number> = [];
  activeDot = signal(1);
  visibleSlides = signal<number[]>([]);

  constructor() {
    afterNextRender(() => {
      this.#createSlider();
    });
  }

  #createSlider() {
    if (this.cards().length <= 0 || this.hide()) {
      return;
    }

    this.#zone.runOutsideAngular(() => {
      this.slider = new KeenSlider(this.sliderRef.nativeElement, {
        mode: 'free-snap',
        initial: this.currentSlide(),
        loop: true,
        slides: {
          spacing: 10,
          perView: () => {
            this.slidesPerView = 3.8;
            this.getShowArrowsDots();
            this.getActiveDot();
            this.updateVisibleSlides();
            return this.slidesPerView;
          },
        },
        breakpoints: {
          '(max-width: 1200px)': {
            loop: true,
            slides: {
              spacing: 10,
              perView: () => {
                this.slidesPerView = 3;
                this.getShowArrowsDots();
                this.getActiveDot();
                this.updateVisibleSlides();
                return this.slidesPerView;
              },
              origin: 'auto',
            },
          },
          '(max-width: 1060px)': {
            loop: true,
            slides: {
              spacing: 10,
              perView: () => {
                this.slidesPerView = 2;
                this.getShowArrowsDots();
                this.getActiveDot();
                this.updateVisibleSlides();
                return this.slidesPerView;
              },
              origin: 'auto',
            },
          },
          '(max-width: 576px)': {
            slides: {
              perView: () => {
                this.slidesPerView = 1;
                this.getShowArrowsDots();
                this.getActiveDot();
                this.updateVisibleSlides();
                return this.slidesPerView;
              },
              origin: 'center',
            },
          },
        },
        slideChanged: (s) => {
          this.#zone.run(() => {
            this.currentSlide.set(s.track.details.rel);
            this.getActiveDot();
            this.updateVisibleSlides();
          });
        },
      });
    });

    this.dotHelper = [...Array(this.slider.track.details.slides.length).keys()];
  }

  ngOnDestroy() {
    if (this.slider) this.slider.destroy();
  }
  cardsTrackByFn(index: number, item: any): number {
    return index;
  }

  getShowArrowsDots() {
    const { arrow = {}, dots = {} } = this.carouselConfig() || {};

    this.showArrows.set(
      !(
        (arrow.mobileHide && this.#screenSizeService.isMobile()) ||
        (arrow.tabletHide && this.#screenSizeService.isTablet()) ||
        (arrow.desktopHide && this.#screenSizeService.isDesktop())
      ),
    );

    this.showDots.set(
      !(
        (dots.mobileHide && this.#screenSizeService.isMobile()) ||
        (dots.tabletHide && this.#screenSizeService.isTablet()) ||
        (dots.desktopHide && this.#screenSizeService.isDesktop())
      ),
    );
  }

  getActiveDot() {
    this.dotHelper.map((dot, i) => {
      if (this.currentSlide() + 1 <= this.slidesPerView * (i + 1) && this.currentSlide() + 1 > this.slidesPerView * i) {
        this.activeDot.set(i);
      }
    });
  }

  getVisibleSlides() {
    let visibleSlides: number[] = [];
    // Round slidesPerView to the closest number
    const slidesPerView = Math.round(this.slidesPerView);

    for (let i = 0; i < slidesPerView; i++) {
      let index = (this.currentSlide() + i) % this.cards.length; // Infinite loop
      visibleSlides.push(index);
    }
    return visibleSlides;
  }

  updateVisibleSlides() {
    this.visibleSlides.set(this.getVisibleSlides());
  }

  isVisibleSlide(index: number): boolean {
    return this.visibleSlides().includes(index);
  }
}
